import moment from 'moment';
import React, { Component } from 'react';
import { DateRangePicker } from 'react-dates';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Button, Card, Col, Row } from 'reactstrap';
import { getAPI } from "../../Base/API";
import { StyledCard, SelectChannelInstance } from "../../Base/Common/CommonUIComponents";
import CustomSelect from '../../Base/Common/CustomSelect';
import { CustomTable } from '../../Base/Common/CustomTable';
import { handleNotification } from "../../Base/Notification";
import NotificationManager from '../../Base/NotificationCenter/NotificationStore';
import ConnectivityIncidentDetailModal from './ConnectivityIncidentDetailModal';
import ConnectivityTestEmailModal from './ConnectivityTestEmailModal';
import { formatDuration, formatIncidentType, formatUtcDate, incidentTypeIcon, renderSeverityBadge, renderStateBadge } from './ConnectivityIncidentsHelpers';

class ConnectivityIncidents extends Component {

    state = {
        block: true,
        modal: false,
        selectedIncident: null,
        incidents: [],
        startDate: moment().subtract(7, 'day'),
        endDate: moment(),
        channelInstanceId: null,
        incidentType: null,
        incidentState: null,
        pageIndex: 0,
        pageSize: 10,
        totalItems: 0,
        channelNames: {},
        hotels: [],
        hotelId: null,
        severity: null,
        testModal: false
    }

    componentDidMount() {
        this.loadHotels();
        this.loadChannelNames(() => {
            this.getIncidents();
            const params = new URLSearchParams(window.location.search);
            const linkedId = params.get('id');
            if (linkedId) this.openIncidentById(parseInt(linkedId, 10));
        });
    }

    openIncidentById = (id) => {
        if (!id || isNaN(id)) return;
        getAPI(result => {
            const { data, error } = result;
            if (error) {
                NotificationManager.error(this.props.intl.formatMessage({ id: "connectivity.deeplink.fetchFailed" }, { message: error.message || error }), '', 3000);
                return;
            }
            if (data && data.errors && data.errors.length > 0) {
                NotificationManager.error(data.errors[0].message, '', 3000);
                return;
            }
            const incident = data && data.responseList && data.responseList[0];
            if (incident) {
                this.setState({ modal: true, selectedIncident: incident });
            } else {
                NotificationManager.error(this.props.intl.formatMessage({ id: "connectivity.deeplink.notFound" }), '', 3000);
            }
        }, `/api/Rate/ConnectivityIncident/v1/${id}`);
    }

    loadHotels = () => {
        getAPI(result => {
            const { data, error } = result;
            if (!error && data && Array.isArray(data.data)) {
                const list = data.data.map(el => ({ value: el.hotelId, label: el.name2 }));
                this.setState({ hotels: list });
            }
        }, '/api/hotel/v1');
    }

    resolveHotelName = (id) => {
        if (!id) return '';
        const h = this.state.hotels.find(x => x.value === id);
        return (h && h.label) || id;
    }

    loadChannelNames = (done) => {
        getAPI(result => {
            const { data, error } = result;
            if (!error && data && Array.isArray(data)) {
                const map = {};
                data.forEach(item => {
                    if (item && item.id != null) {
                        const parts = [item.channelName, item.propertyCode ? `(${item.propertyCode})` : null, item.name].filter(Boolean);
                        map[String(item.id)] = parts.join(' ').trim();
                    }
                });
                this.setState({ channelNames: map }, done);
            } else if (done) {
                done();
            }
        }, '/api/Rate/ChannelConfig/v1/ChannelInstance');
    }

    getIncidents = () => {
        this.setState({ block: true });

        let params = `?pageSize=${this.state.pageSize}&pageIndex=${this.state.pageIndex}`;

        if (this.state.channelInstanceId)
            params += `&channelInstanceId=${encodeURIComponent(this.state.channelInstanceId)}`;
        if (this.state.incidentType)
            params += `&incidentType=${this.state.incidentType}`;
        if (this.state.incidentState)
            params += `&state=${this.state.incidentState}`;
        if (this.state.hotelId)
            params += `&hotelId=${encodeURIComponent(this.state.hotelId)}`;
        if (this.state.severity)
            params += `&severity=${this.state.severity}`;
        if (this.state.startDate)
            params += `&startDate=${this.state.startDate.format('YYYY-MM-DD')}`;
        if (this.state.endDate)
            params += `&endDate=${this.state.endDate.format('YYYY-MM-DD')}`;

        getAPI(result => {
            const { data, error, isLoading } = result;
            this.setState({ block: isLoading });

            if (error) {
                const errorMessage = [{ message: error.message, stack: error.stack, messageType: 'danger' }];
                this.setState({ error: errorMessage, block: false });
                return;
            }
            if (data) {
                if (data.errors && data.errors.length > 0) {
                    handleNotification(data, this.props.intl.formatMessage({ id: "connectivity.search" }), 'danger');
                }

                this.setState({
                    incidents: data.responseList || [],
                    totalItems: data.count || 0,
                    block: false,
                    error: null
                });
            } else {
                this.setState({ block: false });
            }
        }, '/api/Rate/ConnectivityIncident/v1' + params);
    }

    setFilterState = (name, combo) => {
        this.setState({ [name]: combo ? combo.value : null });
    }

    setFilterSelectedChannel = (name, combo) => {
        this.setState({
            channelInstanceId: combo ? combo.value : null
        });
    }

    renderChannelCell = (id) => {
        if (!id) return <FormattedMessage id="connectivity.column.channelNone" />;
        const name = this.state.channelNames[String(id)];
        return (
            <div>
                <div>{name || id}</div>
                <small className="text-muted">#{id}</small>
            </div>
        );
    }

    handleTableChange = (type, { page, sizePerPage }) => {
        this.setState({
            pageIndex: page - 1,
            pageSize: sizePerPage
        }, () => this.getIncidents());
    }

    search = () => {
        this.setState({ pageIndex: 0 }, () => this.getIncidents());
    }

    toggleModal = (incident) => {
        this.setState(prev => {
            const willOpen = !prev.modal;
            const url = new URL(window.location.href);
            if (willOpen && incident) url.searchParams.set('id', incident.id);
            else url.searchParams.delete('id');
            window.history.replaceState({}, '', url.toString());
            return {
                modal: willOpen,
                selectedIncident: incident || null
            };
        });
    }

    renderIncidentTypeCell = (value) => (
        <span>
            <i className={`${incidentTypeIcon(value)} mr-2 text-muted`}></i>
            {formatIncidentType(this.props.intl, value)}
        </span>
    )

    // Substitui o incidente no modal e na lista pelo devolvido pelo endpoint do acknowledge.
    onIncidentUpdated = (updated) => {
        this.setState(prev => ({
            selectedIncident: updated,
            incidents: prev.incidents.map(i => i.id === updated.id ? updated : i)
        }));
    }

    // Icones de estado ack/snooze na coluna da tabela. tooltip com detalhes via title=.
    renderStateWithAck = (state, row) => {
        const nowUtc = moment.utc();
        const snoozeActive = row.snoozedUntil && moment.utc(row.snoozedUntil).isAfter(nowUtc);
        const acked = row.acknowledgedAt != null;
        return (
            <div className="d-flex align-items-center">
                {renderStateBadge(this.props.intl, state)}
                {acked && <i className="fas fa-eye text-success ml-2" title={this.props.intl.formatMessage({ id: "connectivity.status.acknowledged" }, { user: row.acknowledgedByName || row.acknowledgedBy || '' })}></i>}
                {snoozeActive && <i className="fas fa-bell-slash fa-sm text-muted ml-2" title={this.props.intl.formatMessage({ id: "connectivity.status.snoozed" }, { date: formatUtcDate(row.snoozedUntil) })}></i>}
            </div>
        );
    }

    toggleTestModal = () => {
        this.setState(prev => ({ testModal: !prev.testModal }));
    }

    render() {
        const helpContent = (
            <div>
                <FormattedMessage id="connectivity.help" />
                <hr />
            </div>
        );

        const typeOption = (value, msgId) => ({
            value,
            label: (
                <span>
                    <i className={`${incidentTypeIcon(value)} mr-2 text-muted`}></i>
                    <FormattedMessage id={msgId} />
                </span>
            )
        });
        const typeOptions = [
            typeOption('ChannelError', 'connectivity.type.channelError'),
            typeOption('ChannelSendDelay', 'connectivity.type.channelSendDelay'),
            typeOption('ReservationPmsDelay', 'connectivity.type.reservationPmsDelay')
        ];

        const stateOptions = [
            { value: 'Open', label: <FormattedMessage id="connectivity.state.open" /> },
            { value: 'Resolved', label: <FormattedMessage id="connectivity.state.resolved" /> }
        ];

        const severityOptions = [
            { value: 'Critical', label: <FormattedMessage id="connectivity.severity.critical" /> },
            { value: 'Warning', label: <FormattedMessage id="connectivity.severity.warning" /> }
        ];

        const hotelOptions = this.state.hotels || [];

        const columns = [
            {
                dataField: 'hotelId',
                text: this.props.intl.formatMessage({ id: "connectivity.column.hotel" }),
                formatter: cell => this.resolveHotelName(cell)
            },
            {
                dataField: 'channelInstanceId',
                text: this.props.intl.formatMessage({ id: "connectivity.column.channel" }),
                formatter: cell => this.renderChannelCell(cell)
            },
            {
                dataField: 'incidentType',
                text: this.props.intl.formatMessage({ id: "connectivity.column.type" }),
                formatter: cell => this.renderIncidentTypeCell(cell)
            },
            {
                dataField: 'severity',
                text: this.props.intl.formatMessage({ id: "connectivity.column.severity" }),
                formatter: cell => renderSeverityBadge(this.props.intl, cell),
                headerStyle: () => ({ width: "10%" })
            },
            {
                dataField: 'startedAt',
                text: this.props.intl.formatMessage({ id: "connectivity.column.startedAt" }),
                formatter: cell => formatUtcDate(cell),
                headerStyle: () => ({ width: "12%" })
            },
            {
                dataField: 'duration',
                text: this.props.intl.formatMessage({ id: "connectivity.column.duration" }),
                isDummyField: true,
                formatter: (cell, row) => formatDuration(row),
                headerStyle: () => ({ width: "8%" })
            },
            {
                dataField: 'lastDetectedAt',
                text: this.props.intl.formatMessage({ id: "connectivity.column.lastDetectedAt" }),
                formatter: cell => formatUtcDate(cell),
                headerStyle: () => ({ width: "12%" })
            },
            {
                dataField: 'resolvedAt',
                text: this.props.intl.formatMessage({ id: "connectivity.column.resolvedAt" }),
                formatter: cell => formatUtcDate(cell),
                headerStyle: () => ({ width: "12%" })
            },
            {
                dataField: 'state',
                text: this.props.intl.formatMessage({ id: "connectivity.column.state" }),
                formatter: (cell, row) => this.renderStateWithAck(cell, row),
                headerStyle: () => ({ width: "12%" })
            },
            {
                dataField: 'id',
                text: '',
                headerStyle: () => ({ width: "8%", textAlign: 'right' }),
                style: () => ({ textAlign: 'right' }),
                formatter: (cell, row) => (
                    <Button onClick={() => this.toggleModal(row)} className="btn-sm btn-host btn">
                        <FormattedMessage id="connectivity.column.details" />
                    </Button>
                ),
                csvExport: false
            }
        ];

        return (
            <StyledCard block={this.state.block} error={this.state.error} icon={'fas fa-plug'} title={'connectivity.title'} help={helpContent}>
                <Row className="mb-3">
                    <Col md="3" className="px-1">
                        <CustomSelect
                            icon={'fa fa-hotel fa-fw'}
                            options={hotelOptions}
                            value={hotelOptions.find(o => o.value === this.state.hotelId)}
                            onChange={this.setFilterState.bind(this, 'hotelId')}
                            isClearable
                            placeholder={<FormattedMessage id="connectivity.filter.hotel" />}
                        />
                    </Col>
                    <Col md="3" className="px-1">
                        <SelectChannelInstance
                            name={'channelInstanceId'}
                            icon={'fas fa-plug'}
                            value={this.state.channelInstanceId}
                            onChangeFunc={this.setFilterSelectedChannel}
                            isMulti={false}
                            placeholder={<FormattedMessage id="connectivity.filter.channel" />}
                        />
                    </Col>
                    <Col md="6" className="px-1">
                        <DateRangePicker
                            startDate={this.state.startDate ? moment(this.state.startDate).locale(moment.locale()) : this.state.startDate}
                            startDateId="connectivity_start_date_id"
                            isOutsideRange={day => day <= moment().subtract(6, 'month') || day > moment()}
                            endDate={this.state.endDate ? moment(this.state.endDate).locale(moment.locale()) : null}
                            endDateId="connectivity_end_date_id"
                            onDatesChange={({ startDate, endDate }) => this.setState({ startDate: startDate, endDate: endDate })}
                            focusedInput={this.state.focusedInput}
                            onFocusChange={focusedInput => this.setState({ focusedInput: focusedInput })}
                            small={true}
                            numberOfMonths={1}
                            showDefaultInputIcon={true}
                            renderMonthElement={({ month }) => moment(month).locale(this.props.intl.locale).format('MMMM YYYY')}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col md="3" className="px-1">
                        <CustomSelect
                            icon={'fa fa-exclamation-triangle fa-fw'}
                            options={typeOptions}
                            value={typeOptions.find(o => o.value === this.state.incidentType)}
                            onChange={this.setFilterState.bind(this, 'incidentType')}
                            isClearable
                            placeholder={<FormattedMessage id="connectivity.filter.type" />}
                        />
                    </Col>
                    <Col md="3" className="px-1">
                        <CustomSelect
                            icon={'fa fa-signal fa-fw'}
                            options={severityOptions}
                            value={severityOptions.find(o => o.value === this.state.severity)}
                            onChange={this.setFilterState.bind(this, 'severity')}
                            isClearable
                            placeholder={<FormattedMessage id="connectivity.filter.severity" />}
                        />
                    </Col>
                    <Col md="2" className="px-1">
                        <CustomSelect
                            icon={'fa fa-info-circle fa-fw'}
                            options={stateOptions}
                            value={stateOptions.find(o => o.value === this.state.incidentState)}
                            onChange={this.setFilterState.bind(this, 'incidentState')}
                            isClearable
                            placeholder={<FormattedMessage id="connectivity.filter.state" />}
                        />
                    </Col>
                    <Col md="2" className="px-1 ml-auto">
                        <Button className="float-right btn-sm btn-host btn" onClick={this.search}>
                            <i className="fas fa-search"></i>
                        </Button>
                        <Button className="float-right btn-sm btn-host btn mr-2" onClick={this.toggleTestModal} title={this.props.intl.formatMessage({ id: "connectivity.test.button" })}>
                            <i className="fas fa-paper-plane"></i>
                        </Button>
                    </Col>
                </Row>
                <hr />
                <Row>
                    <Col className="h-100">
                        <Card className="border-0">
                            <CustomTable
                                data={this.state.incidents || []}
                                columns={columns}
                                page={this.state.pageIndex + 1}
                                sizePerPage={this.state.pageSize}
                                totalSize={this.state.totalItems}
                                onTableChange={this.handleTableChange}
                                shadow={false}
                                search={false}
                                remote={true}
                                showTotal={true}
                                hideSizePerPage={false}
                                exportCSV={true}
                            />
                        </Card>
                    </Col>
                </Row>

                {this.state.modal ?
                    <ConnectivityIncidentDetailModal
                        modal={this.state.modal}
                        toggle={() => this.toggleModal(null)}
                        incident={this.state.selectedIncident}
                        channelNames={this.state.channelNames}
                        onIncidentUpdated={this.onIncidentUpdated}
                    />
                : ''}

                {this.state.testModal ?
                    <ConnectivityTestEmailModal
                        modal={this.state.testModal}
                        toggle={this.toggleTestModal}
                    />
                : ''}
            </StyledCard>
        );
    }
}

export default injectIntl(ConnectivityIncidents);
